import { BroadcastChannelBrowserBroadcast } from '@adapters/broadcast/broadcastChannelBrowserBroadcast';
import { InmemoryBrowserBroadcast } from '@adapters/broadcast/inmemoryBrowserBroadcast';
import { TagCommanderMetricMapper } from '@adapters/tagging/tagCommanderMetricMapper';
import { TaggingMetricMapper } from '@adapters/tagging/taggingMetricMapper';
import { AngularClient } from '@core/gateway/http/angularClient';
import { AppInsightsLogger } from '@core/gateway/logger/appInsightsLogger';
import { DefaultLogger } from '@core/gateway/logger/defaultLogger';
import { InmemoryLogger } from '@core/gateway/logger/inmemoryLogger';
import { WaitOnCaseStateNotificationSwitcher } from '@core/gateway/notification/caseState/waitOnCaseStateNotificationSwitcher';
import { WaitOnCaseStateNotificationViaPolling } from '@core/gateway/notification/caseState/waitOnCaseStateNotificationViaPolling';
import { WaitOnBankAccountAnalysisCompletionSwitcher } from '@core/gateway/notification/bankAccountAnalysisCompletion/waitOnBankAccountAnalysisCompletionSwitcher';
import { WaitOnWebauthNotificationSwitcher } from '@core/gateway/notification/webauth/waitOnWebauthNotificationSwitcher';
import { WaitOnWebauthNotificationViaPolling } from '@core/gateway/notification/webauth/waitOnWebauthNotificationViaPolling';
import { TagCommanderTracker } from '@core/gateway/tagging/tagCommanderTracker';
import { AccountMapper } from '@core/mapper/accountMapper';
import { BankConnectionLinkedNotificationMapper } from '@core/mapper/bankConnectionLinkedNotificationMapper';
import { BankConnectionMapper } from '@core/mapper/bankConnectionMapper';
import { BankConnectionStateMapper } from '@core/mapper/bankConnectionStateMapper';
import { BankMapper } from '@core/mapper/bankMapper';
import { AccountAnalysisStateMapper } from '@core/mapper/accountAnalysisStateMapper';
import { TaggingTracker } from '@core/model/domain/taggingTracker';
import { NotificationSwitcher } from '@core/model/framework/notifications/notificationSwitcher';
import { DFAccountService } from '@core/service/digital-factory/account/DFAccountService';
import { DFBankService } from '@core/service/digital-factory/bank/DFBankService';
import { DFConsentService } from '@core/service/digital-factory/consent/DFConsentService';
import { DFClerkService } from '@core/service/digital-factory/clerk/DFClerkService';
import { IMAccountService } from '@core/service/in-memory/IMAccountService';
import { IMBankService } from '@core/service/in-memory/IMBankService';
import { IMConsentService } from '@core/service/in-memory/IMConsentService';
import { IMClerkService } from '@core/service/in-memory/IMClerkService';
import { AuthenticateToBankWithCredentials } from '@core/usecase/authenticateToBankWithCredentials';
import { GetAccounts } from '@core/usecase/getAccounts';
import { GetBanks } from '@core/usecase/getBanks';
import { GetTerms } from '@core/usecase/getTerms';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { AbortCase } from '@core/usecase/abortCase';
import { FailCase } from '@core/usecase/failCase';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import { SendConsent } from '@core/usecase/sendConsent';
import { WaitOnBankAccountAnalysisCompletion } from '@core/usecase/waitOnBankAccountAnalysisCompletion';
import { WaitOnWebauthNotification } from '@core/usecase/waitOnWebauthNotification';
import { SERVICE_ID } from '@di/serviceId';
import { Container } from 'inversify';
import type { TagCommanderService } from 'ngx-tag-commander';
import type { Newable } from 'ts-essentials';
import { WaitOnBankAccountAnalysisCompletionViaPolling } from '@core/gateway/notification/bankAccountAnalysisCompletion/waitOnBankAccountAnalysisCompletionViaPolling';
import { GetBankById } from '@core/usecase/getBankById';
import type { KernelConfiguration } from './kernelConfiguration';

export class Kernel {
  private container: Container;

  private constructor(private readonly configuration: KernelConfiguration) {
    this.container = new Container();
  }

  static initialize(configuration: KernelConfiguration): Kernel {
    const kernel = new Kernel(configuration);

    kernel.setupHttpClients();
    kernel.setupServices();
    kernel.setupGateways();
    kernel.setupUsecases();
    kernel.setupMappers();
    kernel.setupConstants();
    return kernel;
  }

  public bindTaggingTracker(tagCommanderService: TagCommanderService): TaggingTracker {
    const taggingContainers = this.container.get(SERVICE_ID.taggingContainers);
    const logger = this.container.get(SERVICE_ID.logger);

    const tagCommanderTracker = new TagCommanderTracker(tagCommanderService, taggingContainers, logger);
    this.container.bind(SERVICE_ID.taggingTracker).toConstantValue(tagCommanderTracker);

    return this.container.get(SERVICE_ID.taggingTracker);
  }

  public bindTaggingMetricMapper(tagCommanderMetricMapper: TagCommanderMetricMapper): TaggingMetricMapper {
    this.container.bind(SERVICE_ID.taggingMetricMapper).toConstantValue(tagCommanderMetricMapper);

    return this.container.get(SERVICE_ID.taggingMetricMapper);
  }

  private setupHttpClients() {
    const openBankingAngularHttpClient = new AngularClient(this.configuration.openBankingBaseUrl);
    this.container.bind(SERVICE_ID.openBankingHttpClient).toConstantValue(openBankingAngularHttpClient);
  }

  private setupServices() {
    if (this.configuration.isInMemory) {
      this.container.bind(SERVICE_ID.bankService).to(IMBankService);
      this.container.bind(SERVICE_ID.accountService).to(IMAccountService);
      this.container.bind(SERVICE_ID.browserBroadcast).to(InmemoryBrowserBroadcast);
      this.container.bind(SERVICE_ID.serverLogger).to(InmemoryLogger);
      this.container.bind(SERVICE_ID.logger).to(InmemoryLogger);
      this.container.bind(SERVICE_ID.consentService).to(IMConsentService);
      this.container.bind(SERVICE_ID.clerkService).to(IMClerkService);
    } else {
      this.container.bind(SERVICE_ID.bankService).to(DFBankService);
      this.container.bind(SERVICE_ID.accountService).to(DFAccountService);
      this.container.bind(SERVICE_ID.browserBroadcast).to(BroadcastChannelBrowserBroadcast);
      this.container.bind(SERVICE_ID.serverLogger).to(AppInsightsLogger);
      this.container.bind(SERVICE_ID.logger).to(DefaultLogger);
      this.container.bind(SERVICE_ID.consentService).to(DFConsentService);
      this.container.bind(SERVICE_ID.clerkService).to(DFClerkService);
    }

    this.container.bind(NotificationSwitcher).toSelf().inSingletonScope();
  }

  private setupUsecases() {
    this.container.bind(SaveWebauthBankConnection).toSelf();
    this.container.bind(AbortCase).toSelf();
    this.container.bind(FailCase).toSelf();
    this.container.bind(GetBanks).toSelf();
    this.container.bind(GetWebauthUrl).toSelf();
    this.container.bind(GetTerms).toSelf();
    this.container.bind(SendConsent).toSelf();
    this.container.bind(GetAccounts).toSelf();
    this.container.bind(AuthenticateToBankWithCredentials).toSelf();
    this.container.bind(GetBankById).toSelf();

    this.container.bind(WaitOnWebauthNotification).toSelf();
    this.container.bind(WaitOnBankAccountAnalysisCompletion).toSelf();

    this.container.bind(WaitOnWebauthNotificationViaPolling).toSelf();
    this.container.bind(WaitOnCaseStateNotificationViaPolling).toSelf();
    this.container.bind(WaitOnBankAccountAnalysisCompletionViaPolling).toSelf();
  }

  private setupMappers() {
    this.container.bind(AccountMapper).toSelf();
    this.container.bind(BankConnectionStateMapper).toSelf();
    this.container.bind(BankMapper).toSelf();
    this.container.bind(BankConnectionMapper).toSelf();
    this.container.bind(BankConnectionLinkedNotificationMapper).toSelf();
    this.container.bind(AccountAnalysisStateMapper).toSelf();
  }

  private setupGateways() {
    this.container.bind(SERVICE_ID.waitOnWebauthNotificationGateway).to(WaitOnWebauthNotificationSwitcher);
    this.container
      .bind(SERVICE_ID.waitOnCaseStateNotificationGateway)
      .to(WaitOnCaseStateNotificationSwitcher);
    this.container
      .bind(SERVICE_ID.waitOnBankAccountAnalysisCompletionGateway)
      .to(WaitOnBankAccountAnalysisCompletionSwitcher);
  }

  private setupConstants() {
    this.container
      .bind(SERVICE_ID.waitOnWebauthTimeoutInSeconds)
      .toConstantValue(this.configuration.notifications.webauth.timeoutInSeconds);
    this.container
      .bind(SERVICE_ID.waitOnWebauthIntervalInSeconds)
      .toConstantValue(this.configuration.notifications.webauth.intervalInSeconds);

    this.container
      .bind(SERVICE_ID.waitOnCaseStateTimeoutInSeconds)
      .toConstantValue(this.configuration.notifications.caseState.timeoutInSeconds);
    this.container
      .bind(SERVICE_ID.waitOnCaseStateIntervalInSeconds)
      .toConstantValue(this.configuration.notifications.caseState.intervalInSeconds);

    this.container
      .bind(SERVICE_ID.waitOnBankAccountAnalysisCompletionTimeoutInSeconds)
      .toConstantValue(this.configuration.notifications.bankAccountAnalysisCompletion.timeoutInSeconds);
    this.container
      .bind(SERVICE_ID.waitOnBankAccountAnalysisCompletionIntervalInSeconds)
      .toConstantValue(this.configuration.notifications.bankAccountAnalysisCompletion.intervalInSeconds);

    this.container.bind(SERVICE_ID.isTrackingEnabled).toConstantValue(this.configuration.isTrackingEnabled);
    this.container.bind(SERVICE_ID.taggingContainers).toConstantValue(this.configuration.taggingContainers);
    this.container.bind(SERVICE_ID.env).toConstantValue(this.configuration.env);
  }

  getByIdentifier<T>(identifier: symbol | Newable<T>): T {
    if (!this.container.isBound(identifier)) {
      throw new Error(`Identifier is not bound ${identifier.toString()}`);
    }
    return this.container.get<T>(identifier);
  }

  isBound<T>(identifier: symbol | Newable<T>): boolean {
    return this.container.isBound(identifier);
  }
}
